import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { sessionApi } from '@/api';
import type { SocialPublishTarget } from '@/api/sessionApi';
import {
  TIMED_TASK_STATUS,
  getTimedTaskStatusText,
} from '@/pages/ai/timedTaskConfig';
import { getStorageUser } from '@/utils/storage';

export interface TimedTaskEditData {
  scheduledTime: string;
  content: string;
  sessionCount: number;
  title: string;
  tags: string;
  annotation: string;
  taskType?: number;
  fetchCount?: number;
  timeRange?: number | string;
  dedup?: boolean;
  sendToFeatured?: boolean;
  sourceIds?: number[];
  aiModel?: string;
  aiStrategy?: string;
  sendSessionIds: number[];
  sessionResults: { id: number; title: string; botAvatar?: string }[];
  draft?: boolean;
  maxExecutionCount?: number | null;
  recurrenceType?: number;
  recurrenceWeekDays?: number[];
  syncToSocial?: number | null;
  socialPublishMode?: 'NONE' | 'MANUAL' | 'AUTO' | null;
  socialPublishTargets?: SocialPublishTarget[] | null;
  t2vModelId?: string;
}

interface SessionStore {
  /** 当前活跃智能体（镜像，由 Chat 页面写入，供子组件只读） */
  currentBot: API.FeimaChatBotListResponse['data']['list'][0];
  /** 侧边栏智能体列表 */
  siderRobotList: any[];
  /** 全量智能体列表（AiHome 等页面用） */
  botList: any[];

  changeBot: (bot: API.FeimaChatBotListResponse['data']['list'][0]) => void;
  changeBotList: (botList: any[]) => void;
  changeSiderBotList: (botList: any[]) => void;
}

// @ts-ignore
export const useSessionStore = create<SessionStore>()(
  persist(
    set => ({
      currentBot: {} as any,
      siderRobotList: [],
      botList: [],

      changeBot: bot => {
        set({ currentBot: bot });
      },
      changeBotList: botList => {
        set({ botList });
      },
      changeSiderBotList: botList => {
        set({ siderRobotList: botList });
      },
    }),
    { name: 'session' }
  )
);

// ── 工具函数（供 useAgentSession 使用） ──────────────────────────────────────

function formatExecuteTime(timestamp: string): string {
  if (!timestamp) return '';
  try {
    if (/^\d+$/.test(timestamp)) {
      const date = new Date(parseInt(timestamp));
      if (isNaN(date.getTime())) return '';
      const y = date.getFullYear();
      const mo = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const mi = String(date.getMinutes()).padStart(2, '0');
      const s = String(date.getSeconds()).padStart(2, '0');
      return `${y}-${mo}-${d} ${h}:${mi}:${s}`;
    }
    return timestamp;
  } catch {
    return '';
  }
}

function getDividerMessageId(item: any): number {
  const text = `${item?.sessionId ?? ''}|${item?.sessionCreateTime ?? ''}|${
    item?.sessionTitle ?? ''
  }`;
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) % 2147483647;
  }
  return hash > 0 ? -hash : -1;
}

function createDividerMessage(item: any) {
  const numericSessionId = Number(item?.sessionId);
  const sessionId = Number.isFinite(numericSessionId) ? numericSessionId : 0;
  return {
    id: getDividerMessageId(item),
    sessionId,
    conversation: '',
    roleType: 'assistant',
    nickname: '',
    avatar: '',
    createTime: item?.sessionCreateTime ?? Date.now(),
    messageType: 'divider',
    skipMemoryNotification: true,
    dividerData: {
      title: item?.sessionTitle || '新会话',
      time: item?.sessionCreateTime,
      sessionId: item?.sessionId,
    },
  };
}

export function processConversationList(
  list: any[],
  currentBot: any,
  currentUser: any
): any[] {
  const result: any[] = [];
  list.forEach((rawItem: any) => {
    if (rawItem?.type === 'DIVIDER') {
      result.push(createDividerMessage(rawItem));
      return;
    }
    const item = rawItem?.type === 'MESSAGE' ? rawItem.conversation : rawItem;
    if (!item) return;
    const isNews =
      (item.conversationExtJsonDTO?.taskInfoExt?.taskType || 1) > 1;
    if (currentBot?.owner && item.conversationExtJsonDTO?.taskInfoExt) {
      const taskInfo = item.conversationExtJsonDTO.taskInfoExt;
      const newsTaskExt =
        taskInfo.newsTaskExt ||
        taskInfo.chatNewsTaskExt ||
        item.conversationExtJsonDTO?.newsTaskExt ||
        {};
      const deduplication = newsTaskExt.deduplication ?? taskInfo.deduplication;
      const sendToSelected =
        newsTaskExt.sendToSelected ?? taskInfo.sendToSelected;
      const formattedTime =
        formatExecuteTime(taskInfo.executeTime) ||
        taskInfo.executeTime ||
        '未知';
      const sessionResults = taskInfo.sessionResults || [];
      const fanNames = sessionResults.map((r: any) => r.title).filter(Boolean);
      result.push({
        ...item,
        _rawId: item.id,
        roleType: 'user',
        avatar:
          currentUser?.avatar ||
          currentUser?.headPic ||
          item.avatar ||
          item.headPic ||
          '',
        messageType: 'timedTask',
        timedTaskData: {
          scheduledTime: formattedTime,
          identity: taskInfo.masterBotName,
          groupName: fanNames.length > 0 ? fanNames.join('、') : '关注列表',
          groupCount: taskInfo.receiveTotal || 0,
          content: item.conversation || '',
          taskId: taskInfo.taskId,
          title: taskInfo.title || '',
          tags: taskInfo.tags || '',
          taskType: taskInfo.taskType || 1,
          annotation: taskInfo.annotation || '',
          fetchCount: newsTaskExt.fetchCount ?? taskInfo.fetchCount,
          timeRange: newsTaskExt.timeRange ?? taskInfo.timeRange,
          dedup: deduplication === undefined ? undefined : deduplication === 1,
          sendToFeatured:
            sendToSelected === undefined ? undefined : sendToSelected === 1,
          sourceIds: newsTaskExt.sourceIds ?? taskInfo.sourceIds ?? [],
          aiModel: newsTaskExt.aiModel ?? taskInfo.aiModel,
          aiStrategy: newsTaskExt.aiStrategy ?? taskInfo.aiStrategy,
          syncToSocial: newsTaskExt.syncToSocial ?? taskInfo.syncToSocial,
          socialPublishMode:
            newsTaskExt.socialPublishMode ?? taskInfo.socialPublishMode,
          socialPublishTargets:
            newsTaskExt.socialPublishTargets ?? taskInfo.socialPublishTargets,
          sendSessionIds: taskInfo.sendSessionIds || [],
          sessionResults,
          newsInfoList: taskInfo.newsInfoList || [],
          maxExecutionCount: taskInfo.maxExecutionCount,
          recurrenceType: taskInfo.recurrenceType,
          recurrenceWeekDays: taskInfo.recurrenceWeekDays,
          t2vModelId: taskInfo.t2vModelId,
        },
      });
      result.push({
        id: item.id ? item.id + 10000 : Math.random(),
        _rawId: item.id,
        sessionId: item.sessionId,
        conversation: '',
        roleType: 'assistant',
        nickname: '智能体',
        avatar: currentBot?.avatar || '',
        createTime: item.createTime,
        messageType: 'timedTaskResult',
        timedTaskResult: {
          taskId: taskInfo.taskId,
          scheduledTime: formattedTime,
          sessionCount: taskInfo.receiveTotal || 0,
          taskStatus: taskInfo.taskStatus,
          taskStatusText: getTimedTaskStatusText(taskInfo.taskStatus),
          recallFlag: taskInfo.recallFlag,
        },
      });
    } else if (!currentBot?.owner && item.conversationExtJsonDTO?.taskInfoExt) {
      const taskInfo = item.conversationExtJsonDTO.taskInfoExt;
      const formattedTime =
        formatExecuteTime(taskInfo.executeTime) ||
        taskInfo.executeTime ||
        '未知';
      const senderName = taskInfo.masterBotName || '未知用户';
      result.push({
        ...item,
        _rawId: item.id,
        roleType: 'assistant',
        nickname: '智能体',
        avatar: currentBot?.avatar || '',
        messageType: 'receivedTaskNotification',
        receivedTaskData: {
          senderName,
          sendTime: formattedTime,
          receiverId: currentUser?.id || '',
          taskContent: item.conversation || '',
          taskDescription:
            !isNews &&
            '将如下文字，复制到你的个人而文件夹（笔记）中，并将该段文字生成你自己的IP短视频',
          taskStatus: taskInfo.taskStatus,
          newsInfoList: taskInfo.newsInfoList || [],
          taskType: taskInfo.taskType || 1,
        },
      });
      if (
        taskInfo.taskStatus !== TIMED_TASK_STATUS.RECALLED &&
        taskInfo.taskStatus !== TIMED_TASK_STATUS.DRAFT &&
        !isNews
      ) {
        result.push({
          id: item.id ? item.id + 10000 : Math.random(),
          _rawId: item.id,
          sessionId: item.sessionId,
          conversation: '',
          roleType: 'assistant',
          nickname: '智能体',
          avatar: currentBot?.avatar || '',
          createTime: item.createTime,
          messageType: 'receivedTaskConfirmation',
          receivedTaskConfirmation: {
            senderName,
            taskContent: item.conversation || '',
            taskDescription:
              !isNews &&
              '将如下文字，复制到你的个人而文件夹（笔记）中，并将该段文字生成你自己的IP短视频',
            taskStatus: taskInfo.taskStatus,
          },
        });
      }
    } else {
      result.push({
        ...item,
        _rawId: item.id,
        avatar: item.avatar || item.headPic || '',
        thinkingContent:
          item.thinkingContent ||
          item.conversationExtJsonDTO?.thinkingContent ||
          undefined,
        toolStatusList:
          (Array.isArray(item.toolStatusList)
            ? item.toolStatusList
            : Array.isArray(item.conversationExtJsonDTO?.toolStatusList)
            ? item.conversationExtJsonDTO.toolStatusList
            : undefined) || undefined,
        memoryList: item.memoryList ?? undefined,
        videoTaskStatus:
          (item as any).videoTaskStatus ||
          item.conversationExtJsonDTO?.videoTaskStatus ||
          undefined,
        videoTaskId:
          (item as any).videoTaskId ||
          item.conversationExtJsonDTO?.videoTaskId ||
          undefined,
      });
    }
  });
  return result;
}

export function mergeDedup(existing: any[], incoming: any[]): any[] {
  const map = new Map(existing.map(item => [item.id, item]));
  for (const item of incoming) {
    map.set(item.id, item);
  }
  return Array.from(map.values());
}
